import type { CSSProperties } from 'react';
import type { SearchResultCustomer, CustomerFormCustomer } from './types';

interface CustomerSearchPanelProps {
  selectedCustomer: CustomerFormCustomer | null;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  searching: boolean;
  showSearchResults: boolean;
  searchResults: SearchResultCustomer[];
  duplicateCustomer: CustomerFormCustomer | null;
  onSelectCustomer: (customer: SearchResultCustomer | CustomerFormCustomer) => void;
  onRestoreCustomer: (customer: CustomerFormCustomer) => void;
  onClearSelection: () => void;
  styles: Record<string, CSSProperties>;
}

export default function CustomerSearchPanel({
  selectedCustomer,
  searchTerm,
  setSearchTerm,
  searching,
  showSearchResults,
  searchResults,
  duplicateCustomer,
  onSelectCustomer,
  onRestoreCustomer,
  onClearSelection,
  styles,
}: CustomerSearchPanelProps) {
  // A retired match is a customer whose record was deleted from the portal. It
  // still owns its email address, so it has to be offered back rather than
  // quietly worked around with a second record.
  const duplicateIsRetired = duplicateCustomer?.status === 'inactive';

  return (
    <>
      {/* Search Existing */}
      {!selectedCustomer && (
        <div style={styles.searchSection}>
          <label style={styles.label}>Search Existing Customers</label>
          <input
            type="text"
            style={styles.input}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Type name, email, or phone to search..."
          />
          {searching && <div style={styles.searchingText}>Searching...</div>}
          {showSearchResults && searchResults.length > 0 && (
            <div style={styles.searchResults}>
              {searchResults.map(customer => (
                <div
                  key={customer.id}
                  style={{
                    ...styles.searchResult,
                    borderLeft: customer.is_own_customer === false ? '3px solid #f59e0b' : undefined,
                  }}
                  onClick={() => onSelectCustomer(customer)}
                >
                  <div className="flex items-center gap-sm">
                    <span style={styles.resultName}>{customer.customer_name}</span>
                    {customer.is_own_customer === false && (
                      <span className="badge badge-warning">
                        {customer.territory_name || 'Unassigned'}
                      </span>
                    )}
                  </div>
                  <div style={styles.resultEmail}>{customer.email}</div>
                  {customer.company_name && (
                    <div style={styles.resultCompany}>{customer.company_name}</div>
                  )}
                </div>
              ))}
            </div>
          )}
          {showSearchResults && searchResults.length === 0 && !searching && (
            <div style={styles.noResults}>No existing customers found</div>
          )}
        </div>
      )}

      {/* Selected Customer Banner */}
      {selectedCustomer && (
        <div style={styles.selectedBanner}>
          <span>Editing: <strong>{selectedCustomer.customer_name}</strong></span>
          <button style={styles.clearBtn} onClick={onClearSelection}>
            Clear & Start New
          </button>
        </div>
      )}

      {/* Duplicate Warning */}
      {duplicateCustomer && !selectedCustomer && (
        <div style={styles.duplicateWarning}>
          <div>
            {duplicateIsRetired ? (
              <>
                <strong>Customer was deleted:</strong> {duplicateCustomer.customer_name} ({duplicateCustomer.email}).
                {' '}They still hold this email address, so a new record cannot use it. Bring them back to carry on.
              </>
            ) : (
              <>
                <strong>Customer exists:</strong> {duplicateCustomer.customer_name} ({duplicateCustomer.email})
              </>
            )}
          </div>
          <button
            style={styles.loadBtn}
            onClick={() => duplicateIsRetired
              ? onRestoreCustomer(duplicateCustomer)
              : onSelectCustomer(duplicateCustomer)}
          >
            {duplicateIsRetired ? 'Restore & Load' : 'Load Details'}
          </button>
        </div>
      )}
    </>
  );
}
